import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Device } from '@twilio/voice-sdk';
import voiceService from '@services/voice.service';
import type { CallParticipant } from '@/features/voice/callConference';
import { saveHandoverNote } from '@/features/voice/wrapUp';

/**
 * A ringing or connected call the agent handles: a Twilio Voice SDK call, or
 * a call the platform bridges itself (BridgeCall). Both offer this surface.
 */
export interface AgentCall {
  accept(): unknown;
  reject(): unknown;
  disconnect(): unknown;
  mute(muted: boolean): unknown;
  /**
   * True when the call can be passed to a colleague after it is answered.
   * Only calls bridged by the platform can; a ringing call always can.
   */
  readonly transferable?: boolean;
  /** True when a colleague can be added to the call once it is answered (a 3-way call). */
  readonly conferenceable?: boolean;
}

export interface IncomingCallContext {
  customerPhone: string;
  handoffReason?: string;
  aiSummary?: Record<string, any>;
  ticketId?: string;
  sessionId?: string;
  /** The colleague who transferred this call here, and their note, if one did. */
  transferredBy?: string;
  transferNote?: string;
  /** This agent transferred the call, nobody took it, and it is back with them. */
  transferReturned?: boolean;
  /** Set when this is an invitation to join a colleague's call: who is asking. */
  invitedBy?: string;
}

/** The call this agent placed to a customer. Apart from incomingCall, which is someone ringing in. */
export interface OutgoingCall {
  callId: string;
  state: string;
  /** Set once the customer answers. Until then there's nothing to wrap up, and no call clock. */
  answeredAt?: string;
}

/** A call the agent has just finished, waiting for its wrap-up. */
export interface WrapUp {
  sessionId?: string;
  ticketId?: string;
  customerPhone: string;
  durationSeconds: number;
  notes: string;
}

interface VoiceAgentState {
  // Device & token
  device: Device | null;
  isDeviceReady: boolean;
  /** Where bridged calls' audio connects, as the server reported it. */
  mediaUrl: string | null;
  /** Why this browser cannot take calls, when the server said so (e.g. voice not set up). */
  phoneError: string | null;
  /** Microphone permission, as last asked. */
  micState: 'unknown' | 'granted' | 'denied';
  /** A short line for the agent about a call that went away (caller hung up, answered elsewhere). */
  notice: string | null;
  /** When the current incoming call started ringing (ms), for the ring clock. */
  ringingSince: number | null;
  /** Notes typed during the current call; saved to the ticket at wrap-up. */
  callNotes: string;
  /** Set when an answered call ends, until the agent saves or skips the wrap-up. */
  wrapUp: WrapUp | null;

  // Agent availability
  isOnline: boolean;

  // Call state
  incomingCall: AgentCall | null;
  activeCall: AgentCall | null;
  outgoingCall: OutgoingCall | null;
  callContext: IncomingCallContext | null;
  isMuted: boolean;
  callDurationSeconds: number;
  /**
   * The call this agent is in the middle of transferring. The server moves
   * the call on before the request returns, and that must not read here as
   * "you missed a call".
   */
  transferringSessionId: string | null;
  /**
   * The call this agent just pressed Accept on in the queue. When its ring
   * arrives it is answered at once - the agent already said yes.
   */
  answeringSessionId: string | null;
  /** Colleagues on (or being added to) this agent's call. */
  participants: CallParticipant[];
  /** True when this agent joined a colleague's call rather than holding it. */
  isGuest: boolean;
  /** The answered call's audio is connected (a colleague can be added from here). */
  callLive: boolean;
  /** The agent chose "Answer & add" on the ring: open "Add to call" once answered. */
  addColleagueOnAnswer: boolean;

  // Actions
  setDevice: (device: Device | null) => void;
  setDeviceReady: (ready: boolean) => void;
  setMediaUrl: (url: string | null) => void;
  setPhoneError: (error: string | null) => void;
  setMicState: (state: 'unknown' | 'granted' | 'denied') => void;
  setNotice: (notice: string | null) => void;
  setCallNotes: (notes: string) => void;
  setWrapUp: (wrapUp: WrapUp | null) => void;
  setOnline: (online: boolean) => void;
  setIncomingCall: (call: AgentCall | null, context?: IncomingCallContext) => void;
  setActiveCall: (call: AgentCall | null) => void;
  setOutgoingCall: (outgoingCall: OutgoingCall | null) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  hangup: () => void;
  /**
   * Transfer the ringing or connected call to a colleague (or, with no
   * agentId, to the next available agent). Rejects when the server refuses;
   * the call is then untouched and still this agent's.
   */
  transferCall: (to: { agentId?: string; name?: string }, note?: string) => Promise<void>;
  /**
   * Take a caller from the queue view - waiting, or ringing for a colleague -
   * and answer it as soon as it reaches this agent. Rejects when another agent
   * got there first; nothing changes for this agent then.
   */
  takeCall: (sessionId: string) => Promise<void>;
  setAnsweringSessionId: (sessionId: string | null) => void;
  /**
   * Add a colleague to the current call (or, with no agentId, whoever is free
   * next). Rejects when the server refuses; the call is then unchanged.
   */
  addToCall: (to: { agentId?: string; name?: string }) => Promise<void>;
  /** Cancel a colleague's invitation, or take them off the call. */
  removeFromCall: (agentId: string) => Promise<void>;
  setParticipants: (participants: CallParticipant[]) => void;
  setGuest: (isGuest: boolean) => void;
  setCallLive: (live: boolean) => void;
  setAddColleagueOnAnswer: (add: boolean) => void;
  mute: () => void;
  unmute: () => void;
  setCallDuration: (seconds: number) => void;
  reset: () => void;
}

/** How long a call taken from the queue may take to ring before we stop waiting to answer it. */
const ANSWER_WAIT_MS = 45_000;

/**
 * The wrap-up for the call that is ending, if there was an answered one: its
 * ticket, the caller, how long it lasted and the notes typed during it. Empty
 * when no answered call is ending, or one is already waiting for wrap-up.
 */
function wrapUpOf(state: VoiceAgentState): Partial<VoiceAgentState> {
  const { activeCall, callContext, wrapUp } = state;
  if (!activeCall || !callContext || wrapUp) return {};
  // A customer who never answered: nobody spoke, so there is nothing to record.
  if (state.outgoingCall && !state.outgoingCall.answeredAt) return {};
  // A dialled number has no ticket for the outcome and notes to go on.
  if (state.outgoingCall && !callContext.ticketId) return { callNotes: '' };
  // Handed on - transferred, or left with a colleague keeping the caller - or
  // joined as a colleague: the call goes on without this agent, so there is
  // no outcome to record (see handOver).
  const handingOver = !!state.transferringSessionId && state.transferringSessionId === callContext.sessionId;
  if (handingOver || state.isGuest) return {};
  return {
    wrapUp: {
      sessionId: callContext.sessionId,
      ticketId: callContext.ticketId,
      customerPhone: callContext.customerPhone,
      durationSeconds: state.callDurationSeconds,
      notes: state.callNotes,
    },
    callNotes: '',
  };
}

export const useVoiceAgentStore = create<VoiceAgentState>()(
  persist(
    (set, get) => {
      /**
       * This agent leaves a call that carries on with a colleague. No wrap-up
       * (there is no outcome yet); what they wrote during the call is added to
       * the ticket as an internal note, without touching its status.
       */
      const handOver = (toName: string | undefined, notice: string) => {
        const { activeCall, callContext, callNotes, callDurationSeconds } = get();
        if (!activeCall || !callContext) return;
        const call = {
          sessionId: callContext.sessionId,
          ticketId: callContext.ticketId,
          customerPhone: callContext.customerPhone,
          durationSeconds: callDurationSeconds,
          notes: callNotes,
        };
        // Marks the call as handed on while it disconnects, so nothing on the
        // way out (the phone's own disconnect handler) opens a wrap-up.
        set({ transferringSessionId: callContext.sessionId });
        activeCall.disconnect();
        set({
          activeCall: null,
          callContext: null,
          isMuted: false,
          callDurationSeconds: 0,
          callNotes: '',
          participants: [],
          isGuest: false,
          callLive: false,
          addColleagueOnAnswer: false,
          notice,
          transferringSessionId: null,
        });
        if (!callNotes.trim()) return;
        saveHandoverNote(call, callNotes, toName)
          .then(() => {
            if (get().notice === notice) set({ notice: `${notice} Your notes were added to the ticket.` });
          })
          .catch(() => {
            set({ notice: `${notice} Your notes could not be saved to the ticket; please add them there.` });
          });
      };

      return {
      device: null,
      isDeviceReady: false,
      mediaUrl: null,
      phoneError: null,
      micState: 'unknown',
      notice: null,
      ringingSince: null,
      callNotes: '',
      wrapUp: null,
      isOnline: false,
      incomingCall: null,
      activeCall: null,
      outgoingCall: null,
      callContext: null,
      isMuted: false,
      callDurationSeconds: 0,
      transferringSessionId: null,
      answeringSessionId: null,
      participants: [],
      isGuest: false,
      callLive: false,
      addColleagueOnAnswer: false,

      setDevice: (device) => set({ device }),
      setAnsweringSessionId: (answeringSessionId) => set({ answeringSessionId }),
      setDeviceReady: (ready) => set({ isDeviceReady: ready }),
      setMediaUrl: (url) => set({ mediaUrl: url }),
      setPhoneError: (error) => set({ phoneError: error }),
      setMicState: (micState) => set({ micState }),
      setNotice: (notice) => set({ notice }),
      setCallNotes: (callNotes) => set({ callNotes }),
      setWrapUp: (wrapUp) => set({ wrapUp }),
      setOnline: (online) => set({ isOnline: online }),

      setIncomingCall: (call, context) =>
        set({
          incomingCall: call,
          callContext: context ?? null,
          ringingSince: call ? Date.now() : null,
          ...(call && {
            notice: null,
            participants: [],
            isGuest: !!context?.invitedBy,
            callLive: false,
            addColleagueOnAnswer: false,
          }),
        }),

      setParticipants: (participants) => set({ participants }),
      setGuest: (isGuest) => set({ isGuest }),
      setCallLive: (callLive) => set({ callLive }),
      setAddColleagueOnAnswer: (addColleagueOnAnswer) => set({ addColleagueOnAnswer }),

      addToCall: async (to) => {
        const sessionId = get().callContext?.sessionId;
        if (!get().activeCall || !sessionId) throw new Error('There is no call to add a colleague to');
        await voiceService.inviteToCall(sessionId, { targetAgentId: to.agentId });
        // The server's update fills in who and their attempt; until then, show the ring.
        if (to.agentId && !get().participants.some((p) => p.agentId === to.agentId)) {
          set({
            participants: [
              ...get().participants,
              { agentId: to.agentId, agentName: to.name, attemptId: '', state: 'ringing' },
            ],
          });
        }
      },

      removeFromCall: async (agentId) => {
        const sessionId = get().callContext?.sessionId;
        if (!sessionId) return;
        await voiceService.removeFromCall(sessionId, agentId);
        set({ participants: get().participants.filter((p) => p.agentId !== agentId) });
      },

      setActiveCall: (call) =>
        set({
          ...(call
            ? {}
            : {
                ...wrapUpOf(get()),
                outgoingCall: null,
                participants: [],
                isGuest: false,
                callLive: false,
                addColleagueOnAnswer: false,
              }),
          activeCall: call,
          isMuted: false,
          callDurationSeconds: 0,
        }),

      setOutgoingCall: (outgoingCall) => set({ outgoingCall }),

      acceptCall: () => {
        const { incomingCall } = get();
        if (!incomingCall) return;
        incomingCall.accept();
        set({ activeCall: incomingCall, incomingCall: null, callNotes: '', wrapUp: null });
      },

      rejectCall: () => {
        const { incomingCall, callContext } = get();
        if (!incomingCall) return;
        incomingCall.reject();
        set({ incomingCall: null, callContext: null });
        // Tell the server too, so the caller is offered to the next agent now
        // rather than after the ring timeout.
        if (callContext?.sessionId) {
          voiceService.declineHandoff(callContext.sessionId).catch(() => {});
        }
      },

      hangup: () => {
        const { activeCall, participants, isGuest, callContext, outgoingCall } = get();
        if (!activeCall) return;
        if (outgoingCall) {
          // Still ringing, it can't be cancelled, and going now would leave the
          // customer to answer to nobody. Once answered, the server ends it at
          // once, rather than after the wait for a dropped connection to return.
          if (!outgoingCall.answeredAt) return;
          voiceService.endOutboundCall(outgoingCall.callId).catch(() => {});
        }
        // Leaving a call a colleague has joined: they keep the caller.
        const keptBy = isGuest ? undefined : participants.find((p) => p.state === 'joined');
        if (keptBy && callContext?.sessionId) {
          handOver(keptBy.agentName, `${keptBy.agentName || 'Your colleague'} has the call now.`);
          return;
        }
        const ending = wrapUpOf(get());
        activeCall.disconnect();
        set({
          ...ending,
          activeCall: null,
          outgoingCall: null,
          callContext: null,
          isMuted: false,
          callDurationSeconds: 0,
          participants: [],
          isGuest: false,
          callLive: false,
          addColleagueOnAnswer: false,
        });
      },

      takeCall: async (sessionId) => {
        set({ answeringSessionId: sessionId });
        try {
          await voiceService.claimHandoff(sessionId);
        } catch (error) {
          set({ answeringSessionId: null });
          throw error;
        }
        // The ring normally arrives in a second or two. If it never does
        // (the phone could not be rung), stop waiting to answer it.
        setTimeout(() => {
          if (get().answeringSessionId === sessionId) set({ answeringSessionId: null });
        }, ANSWER_WAIT_MS);
      },

      transferCall: async (to, note) => {
        const { incomingCall, activeCall, callContext } = get();
        const call = incomingCall ?? activeCall;
        const sessionId = callContext?.sessionId;
        if (!call || !sessionId) throw new Error('There is no call to transfer');
        const wasRinging = !!incomingCall;

        set({ transferringSessionId: sessionId });
        try {
          await voiceService.transferCall(sessionId, { targetAgentId: to.agentId, note });
        } catch (error) {
          set({ transferringSessionId: null });
          throw error;
        }

        const notice = to.name
          ? `Call transferred to ${to.name}.`
          : 'Call transferred to the next available agent.';
        if (wasRinging) {
          // Not a decline: the server has already moved the call on.
          call.reject();
          set({ incomingCall: null, callContext: null, notice, transferringSessionId: null });
          return;
        }
        // Not the end of the call: no wrap-up. The agent's notes still go to the ticket.
        handOver(to.name, notice);
      },

      mute: () => {
        const { activeCall } = get();
        if (!activeCall) return;
        activeCall.mute(true);
        set({ isMuted: true });
      },

      unmute: () => {
        const { activeCall } = get();
        if (!activeCall) return;
        activeCall.mute(false);
        set({ isMuted: false });
      },

      setCallDuration: (seconds) => set({ callDurationSeconds: seconds }),

      reset: () =>
        set({
          ...wrapUpOf(get()),
          incomingCall: null,
          activeCall: null,
          outgoingCall: null,
          callContext: null,
          isMuted: false,
          callDurationSeconds: 0,
          participants: [],
          isGuest: false,
          callLive: false,
          addColleagueOnAnswer: false,
        }),
      };
    },
    {
      name: 'voice-agent-online-state',
      // Only persist isOnline — device/call state is always transient
      partialize: (state) => ({ isOnline: state.isOnline }),
    }
  )
);